import { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { Button } from '../ui';
import { ShoppingBag } from 'lucide-react';
import { CloseIcon, GearsIcon, ShieldIcon, PuzzleIcon, WalletIcon } from '../icons';
import { LanguageSwitcher } from './LanguageSwitcher';
import styles from './MobileMenu.module.css';

interface SubItem {
  label: string;
  description: string;
  hash: string;
  path?: string;
  external?: string;
}

interface NavItem {
  to: string;
  label: string;
  items?: SubItem[];
  menuOnly?: boolean;
}

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  links: NavItem[];
}

const subIcons: Record<string, React.ReactNode> = {
  'solutions': <PuzzleIcon size={16} />,
  'business-model': <GearsIcon size={16} />,
  'conformite': <ShieldIcon size={16} />,
  'shop-pro': <WalletIcon size={16} />,
  'shop-perso': <ShoppingBag size={16} strokeWidth={1.5} />,
};

export function MobileMenu({ isOpen, onClose, links }: MobileMenuProps) {
  const { t } = useTranslation('common');
  const { lp } = useLocalizedPath();

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const top = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) window.scrollTo(0, parseInt(top, 10) * -1);
    }
    return () => {
      const top = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (top) window.scrollTo(0, parseInt(top, 10) * -1);
    };
  }, [isOpen]);

  return (
    <div className={`${styles.overlay} ${isOpen ? styles.open : ''}`}>
      <div className={styles.menu}>
        <div className={styles.header}>
          <Link to={lp('/')} onClick={onClose} className={styles.logo}>
            <img
              src="/images/logos/logo-seul-noir.webp"
              alt="QBP.Solutions"
              width={40}
              height={37}
              className={styles.logoImage}
            />
          </Link>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('aria.closeMenu')}
          >
            <CloseIcon />
          </button>
        </div>

        <nav className={styles.nav}>
          {links.map((link) => (
            <div key={link.to} className={styles.navGroup}>
              {link.menuOnly ? (
                <span className={styles.navLink}>{link.label}</span>
              ) : (
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.active : ''}`
                  }
                  onClick={onClose}
                >
                  {link.label}
                </NavLink>
              )}
              {link.items && (
                <div className={styles.subLinks}>
                  {link.items.map((sub) => {
                    const inner = (
                      <>
                        <span className={styles.subIcon}>{subIcons[sub.hash]}</span>
                        <span className={styles.subText}>
                          <span className={styles.subLabel}>{sub.label}</span>
                          <span className={styles.subDesc}>{sub.description}</span>
                        </span>
                      </>
                    );
                    return sub.external ? (
                      <a
                        key={sub.hash}
                        href={sub.external}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.subLink}
                        onClick={onClose}
                      >
                        {inner}
                      </a>
                    ) : (
                      <Link
                        key={sub.hash}
                        to={sub.path ? lp(sub.path) : `${link.to}#${sub.hash}`}
                        className={styles.subLink}
                        onClick={onClose}
                      >
                        {inner}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className={styles.divider} />

        <div className={styles.footer}>
          <LanguageSwitcher onNavigate={onClose} />
          <Button href={lp('/contact')} onClick={onClose} className={styles.ctaFooter}>
            {t('nav.cta')}
          </Button>
        </div>
      </div>
    </div>
  );
}
